import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronRight, Home as HomeIcon } from "lucide-react";
import { useEntityCrumb } from "../contexts/UXChromeContext";
import useModules from "../hooks/useModules";

const STATIC_LABELS = {
    "staff": "Staff",
    "attendance": "Attendance",
    "exceptions": "Exception Queue",
    "shift-rules": "Shift Rules",
    "biometric-devices": "Biometric Devices",
    "contracts": "Contracts",
    "wage-rates": "Wage Rates",
    "payroll": "Payroll",
    "funding": "Payroll Funding",
    "finance": "Finance",
    "reports": "Reports",
    "audit": "Audit Log",
    "users": "Users",
    "departments": "Departments",
    "master-finance": "Master Finance",
    "admin": "Admin",
    "settings": "Settings",
};

function isUuid(s) {
    return /^[0-9a-f-]{20,}$/i.test(s);
}

function labelFor(seg) {
    return STATIC_LABELS[seg] || seg.charAt(0).toUpperCase() + seg.slice(1).replace(/-/g, " ");
}

export default function Breadcrumbs() {
    const location = useLocation();
    const { byId: modulesById } = useModules();
    const { entityCrumb } = useEntityCrumb();

    const crumbs = useMemo(() => {
        const path = location.pathname;
        if (path === "/" || path === "/login" || path === "/forbidden" || path === "/404") return null;

        const parts = path.split("/").filter(Boolean);
        const out = [{ label: "Home", to: "/", testid: "crumb-home" }];

        // Special handling for /m/:code/...
        if (parts[0] === "m" && parts[1]) {
            const code = parts[1];
            const mod = Object.values(modulesById || {}).find((m) => m.code?.toLowerCase() === code.toLowerCase());
            const modLabel = mod ? mod.name : code.toUpperCase();
            out.push({ label: modLabel, to: `/m/${code}`, testid: "crumb-module" });

            for (let i = 2; i < parts.length; i++) {
                const seg = parts[i];
                const accumulated = "/" + parts.slice(0, i + 1).join("/");
                if (isUuid(seg)) {
                    out.push({ label: entityCrumb || seg.slice(0, 8), to: accumulated, testid: "crumb-entity" });
                } else {
                    out.push({ label: labelFor(seg), to: accumulated, testid: `crumb-${seg}` });
                }
            }
            return out;
        }

        // Generic flat paths e.g. /master-finance, /admin/users, /users, /audit
        for (let i = 0; i < parts.length; i++) {
            const seg = parts[i];
            const accumulated = "/" + parts.slice(0, i + 1).join("/");
            if (isUuid(seg)) {
                out.push({ label: entityCrumb || seg.slice(0, 8), to: accumulated, testid: "crumb-entity" });
            } else {
                out.push({ label: labelFor(seg), to: accumulated, testid: `crumb-${seg}` });
            }
        }
        return out;
    }, [location.pathname, modulesById, entityCrumb]);

    if (!crumbs || crumbs.length <= 1) return null;

    return (
        <nav aria-label="breadcrumb" data-testid="breadcrumbs" className="px-8 py-2 bg-white text-xs"
            style={{ borderBottom: "1px solid var(--asl-border)" }}>
            <ol className="flex items-center gap-1.5 flex-wrap">
                {crumbs.map((c, idx) => {
                    const last = idx === crumbs.length - 1;
                    return (
                        <li key={idx} className="flex items-center gap-1.5">
                            {idx > 0 && <ChevronRight className="h-3 w-3 text-[color:var(--asl-muted)]" />}
                            {idx === 0 ? (
                                last ? (
                                    <span className="asl-navy-text font-medium flex items-center gap-1" data-testid={c.testid}>
                                        <HomeIcon className="h-3 w-3" /> {c.label}
                                    </span>
                                ) : (
                                    <a href={c.to} className="text-[color:var(--asl-muted)] hover:asl-navy-text transition flex items-center gap-1"
                                        data-testid={c.testid}>
                                        <HomeIcon className="h-3 w-3" /> {c.label}
                                    </a>
                                )
                            ) : last ? (
                                <span className="asl-navy-text font-semibold" data-testid={c.testid}>{c.label}</span>
                            ) : (
                                <a href={c.to} className="text-[color:var(--asl-muted)] hover:asl-navy-text transition"
                                    data-testid={c.testid}>{c.label}</a>
                            )}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
}
